import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const CURRENT_YEAR = new Date().getFullYear();
const DEFAULT_REPORT_YEAR = CURRENT_YEAR - 1;
const YEARS = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR - i);

function VendorBilling({ companyName }) {
  const [selectedMonth, setSelectedMonth] = useState(0);
  const [selectedYear, setSelectedYear] = useState(DEFAULT_REPORT_YEAR);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [emptyHint, setEmptyHint] = useState('');
  const [billingData, setBillingData] = useState(null);

  useEffect(() => {
    fetchBillingData();
  }, [selectedMonth, selectedYear, companyName]);

  const fetchBillingData = async () => {
    setLoading(true);
    setError('');
    try {
      // Get JWT token from localStorage
      const token = localStorage.getItem('gogig_token');
      
      if (!token) {
        setError('Authentication required. Please log in again.');
        return;
      }

      console.log('VendorBilling - Using token:', token.substring(0, 50) + '...');

      // Call the new company vendor billing endpoint
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';
      const response = await fetch(
        `${API_URL}/api/company/vendor-billing?month=${selectedMonth + 1}&year=${selectedYear}`, 
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || 'Failed to fetch billing data');
      }

      const data = await response.json();
      console.log('VendorBilling - API Response:', data);

      if (!data.success) {
        setBillingData(null);
        setError(data.message || 'No billing data available for this period');
        return;
      }

      // Use the billing data (may be empty when no Aisect/Eduvantage records for this period)
      const billingRecords = data.billing?.billing || [];
      const financeNames = data.billing?.financeClientNames || [];
      const linkedInvoices = data.billing?.linkedInvoiceNumbers || [];
      const sourceCheck = data.billing?.sourceCheck || null;
      const linkedBySource = data.billing?.linkedInvoicesBySource || { aisect: [], eduvantage: [] };

      setBillingData({
        billing: billingRecords,
        totalRecords: data.billing?.totalRecords ?? billingRecords.length,
        client: data.billing?.client || null,
        financeClientNames: financeNames,
        linkedInvoiceNumbers: linkedInvoices,
        linkedInvoicesBySource: linkedBySource,
        sourceCheck,
      });

      if (billingRecords.length === 0) {
        const clientLabel = data.billing?.client?.companyName || 'your finance client';
        const period = `${MONTHS[selectedMonth]} ${selectedYear}`;
        const aisectLinked = linkedBySource.aisect || [];
        const eduvantageLinked = linkedBySource.eduvantage || [];
        const aisectSaved = sourceCheck?.aisect?.vendorRecordsSaved ?? 0;
        const eduvantageSaved = sourceCheck?.eduvantage?.vendorRecordsSaved ?? 0;

        if (aisectLinked.length > 0 || eduvantageLinked.length > 0) {
          const parts = [];
          if (aisectLinked.length > 0) {
            parts.push(`Aisect sales invoice${aisectLinked.length > 1 ? 's' : ''} ${aisectLinked.join(', ')}${aisectSaved ? '' : ' (vendor calc not saved)'}`);
          }
          if (eduvantageLinked.length > 0) {
            parts.push(`Eduvantage sales invoice${eduvantageLinked.length > 1 ? 's' : ''} ${eduvantageLinked.join(', ')}${eduvantageSaved ? '' : ' (vendor calc not saved)'}`);
          }
          setEmptyHint(`Checked both Aisect and Eduvantage for ${clientLabel} (${period}). ${parts.join('; ')}. Ask finance to save vendor billing in the module where the invoice exists.`);
        } else if (selectedYear === CURRENT_YEAR) {
          setEmptyHint(`Checked both Aisect and Eduvantage — no billing for ${period}. Try year ${CURRENT_YEAR - 1}. Finance client: ${clientLabel}.`);
        } else {
          setEmptyHint(`Checked both Aisect and Eduvantage — no vendor billing for ${period} linked to finance client "${clientLabel}".`);
        }
      } else {
        setEmptyHint('');
      }
      setError('');
    } catch (err) {
      // Handle connection errors
      if (err.message === 'Failed to fetch' || err.name === 'TypeError' || err.message.includes('ERR_CONNECTION_REFUSED')) {
        // Only log in development mode to reduce console noise
        if (process.env.NODE_ENV === 'development') {
          console.warn('Connection error: Backend server may not be running on port 5001');
        }
        setError('Cannot connect to server. Please ensure the backend server is running on port 5001.');
        setBillingData(null);
      } else if (err.message.includes('Establishment code not set')) {
        setError('Please set your establishment code in your profile to view billing data.');
        setBillingData(null);
      } else if (err.message.includes('not found') || err.message.includes('404')) {
        setError('No billing data found for this period. The billing feature may not be fully configured yet.');
        setBillingData(null);
      } else if (err.message.includes('401') || err.message.includes('403') || err.message.includes('Invalid token') || err.message.includes('Unauthorized')) {
        localStorage.removeItem('gogig_token');
        localStorage.removeItem('gogig_user');
        setError('Session expired. Please log in again.');
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
        setBillingData(null);
      } else {
        // Log other errors for debugging
        console.error('Error fetching billing data:', err);
        setError('Error fetching billing data: ' + (err.message || 'Unknown error occurred'));
        setBillingData(null);
      }
    } finally {
      setLoading(false);
    }
  };

  // Helper function to extract value from calculation data
  const getCalculationValue = (calculationData, label) => {
    const sources = [];
    if (Array.isArray(calculationData)) {
      sources.push(calculationData);
    } else if (calculationData && typeof calculationData === 'object') {
      if (Array.isArray(calculationData.vendorCalc)) sources.push(calculationData.vendorCalc);
      if (Array.isArray(calculationData.main)) sources.push(calculationData.main);
      const summary = calculationData.summary;
      if (summary && Array.isArray(summary.main)) sources.push(summary.main);
      if (summary && Array.isArray(summary.stipendSummary)) sources.push(summary.stipendSummary);
    }
    const labelLower = label.toLowerCase();
    for (const arr of sources) {
      const item = arr.find((row) => String(row?.label || '').trim().toLowerCase() === labelLower);
      if (item && item.value != null && item.value !== '') {
        const n = parseFloat(String(item.value).replace(/,/g, ''));
        if (!isNaN(n)) return n;
      }
    }
    return 0;
  };

  const renderBillingDetails = () => {
    if (!billingData) return null;

    // Display billing records (from both Aisect and Eduvantage)
    const billingRecords = billingData.billing || [];
    
    // Debug logging
    console.log('Billing data structure:', billingData);
    console.log('Billing records:', billingRecords);
    if (billingRecords.length > 0) {
      console.log('First record calculation data:', billingRecords[0].calculationData);
    }

    return (
      <Grid container spacing={3}>
        {/* Billing Records */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Billing Records ({billingRecords.length} record{billingRecords.length !== 1 ? 's' : ''})
                {billingRecords.length === 0 && ' — No Aisect or Eduvantage billing for this period'}
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Source</TableCell>
                      <TableCell>Invoice Number</TableCell>
                      <TableCell>Month/Year</TableCell>
                      <TableCell>Taxable Amount</TableCell>
                      <TableCell>GST</TableCell>
                      <TableCell>Total Amount</TableCell>
                      <TableCell>Candidate Count</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {billingRecords.map((record, index) => (
                      <TableRow key={record.source ? `${record.source}-${record.invoiceNumber}-${index}` : index}>
                        <TableCell>
                          {record.source === 'eduvantage' ? 'Eduvantage' : record.source === 'aisect' ? 'Aisect' : '—'}
                        </TableCell>
                        <TableCell>{record.invoiceNumber}</TableCell>
                        <TableCell>{record.month}/{record.year}</TableCell>
                        <TableCell>₹{getCalculationValue(record.calculationData?.vendorCalc || record.calculationData, "Taxable")}</TableCell>
                        <TableCell>₹{getCalculationValue(record.calculationData?.vendorCalc || record.calculationData, "GST")}</TableCell>
                        <TableCell>₹{getCalculationValue(record.calculationData?.vendorCalc || record.calculationData, "Total Amount")}</TableCell>
                        <TableCell>
                          {record.candidateCount > 0
                            ? record.candidateCount
                            : getCalculationValue(record.calculationData, 'Candidate Count')
                              || getCalculationValue(record.calculationData, 'No of Candidates')
                              || getCalculationValue(record.calculationData, 'Total Candidates')
                              || 0}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Client Information */}
        {billingData.client && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Client Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">
                      Company: {billingData.client.companyName}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">
                      Establishment ID: {billingData.client.establishmentId}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">
                      Location: {billingData.client.location}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    );
  };

  return (
    <Box sx={{ py: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Monthly Billing Details
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Billing from both Aisect and Eduvantage vendor billing (merged for your establishment)
        </Typography>
      </Box>

      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Month</InputLabel>
            <Select
              value={selectedMonth}
              label="Month"
              onChange={(e) => setSelectedMonth(e.target.value)}
            >
              {MONTHS.map((month, index) => (
                <MenuItem key={month} value={index}>
                  {month}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Year</InputLabel>
            <Select
              value={selectedYear}
              label="Year"
              onChange={(e) => setSelectedYear(e.target.value)}
            >
              {YEARS.map(year => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="info" sx={{ mb: 4 }}>
          {error}
        </Alert>
      ) : (
        <>
          {emptyHint && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              {emptyHint}
            </Alert>
          )}
          {renderBillingDetails()}
        </>
      )}
    </Box>
  );
}

export default VendorBilling;